import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle, XCircle, Trash2, Eye, RefreshCw, Clock, MapPin, Edit, MoreVertical, Archive, Users, ChevronDown, ChevronRight, Coffee, Play, Search, Download } from 'lucide-react';
import UploadDetailView from './UploadDetailView';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const TimeAuditUploads = ({ isViewer }) => {
  const [uploads, setUploads] = useState([]);
  const [violations, setViolations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('uploads'); // 'uploads', 'violations', or 'employees'
  const [violationFilters, setViolationFilters] = useState({
    status: '',
    severity: '',
    violationType: '',
    uploadId: ''
  });
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editingViolationId, setEditingViolationId] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openDropdownUpload, setOpenDropdownUpload] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [employeesTabUploadId, setEmployeesTabUploadId] = useState('all');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [expandedViolationId, setExpandedViolationId] = useState(null);
  const [violationDetails, setViolationDetails] = useState({});
  const [markingSalariedId, setMarkingSalariedId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchUploads();
    fetchSummary();
  }, [includeArchived]);

  useEffect(() => {
    if (activeTab === 'violations') {
      fetchViolations();
    }
  }, [activeTab, violationFilters]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('.dropdown-trigger') && !event.target.closest('.dropdown-menu')) {
        setOpenDropdownId(null);
        setOpenDropdownUpload(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  // Auto-select 'all' for employees tab if nothing selected
  useEffect(() => {
    if (!employeesTabUploadId) {
      setEmployeesTabUploadId('all');
    }
  }, [uploads]);

  const fetchUploads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', '50');
      if (includeArchived) {
        params.append('includeArchived', 'true');
      }
      const response = await axios.get(`/api/time-audit/uploads?${params.toString()}`);
      setUploads(response.data.uploads || []);
    } catch (error) {
      console.error('Error fetching uploads:', error);
      setMessage({ type: 'error', text: 'Failed to load uploads' });
    } finally {
      setLoading(false);
    }
  };

  const fetchViolations = async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (violationFilters.status) params.append('status', violationFilters.status);
      if (violationFilters.severity) params.append('severity', violationFilters.severity);
      if (violationFilters.violationType) params.append('violationType', violationFilters.violationType);
      if (violationFilters.uploadId) params.append('uploadId', violationFilters.uploadId);

      const response = await axios.get(`/api/time-audit/violations?${params.toString()}`);
      setViolations(response.data.violations || []);
    } catch (error) {
      console.error('Error fetching violations:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get('/api/time-audit/violations/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'xls' && ext !== 'xlsx') {
      setMessage({ type: 'error', text: 'Please select an Excel file (.xls or .xlsx)' });
      return;
    }

    try {
      setUploading(true);
      setMessage({ type: '', text: '' });

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post('/api/time-audit/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setMessage({
        type: 'success',
        text: `File processed: ${response.data.entriesCount} entries, ${response.data.violationsCount} violations found`
      });

      fetchUploads();
      fetchSummary();
    } catch (error) {
      console.error('Error uploading file:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.details || error.response?.data?.error || 'Failed to upload file'
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteUpload = async (uploadId, fileName) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${fileName}" and all related data? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`/api/time-audit/uploads/${uploadId}`);
      setMessage({ type: 'success', text: 'Upload deleted successfully' });
      fetchUploads();
      fetchSummary();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error deleting upload:', error);
      setMessage({ type: 'error', text: 'Failed to delete upload' });
    }
  };

  const handleArchiveUpload = async (uploadId, fileName, shouldActivate) => {
    const action = shouldActivate ? 'unarchive' : 'archive';
    const confirmMessage = `Are you sure you want to ${action} "${fileName}"?${!shouldActivate ? '\n\nArchived uploads will not send email notifications.' : ''}`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await axios.put(`/api/time-audit/uploads/${uploadId}`, { isActive: shouldActivate });
      setMessage({ type: 'success', text: `Upload ${shouldActivate ? 'unarchived' : 'archived'} successfully` });
      fetchUploads();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error archiving upload:', error);
      setMessage({ type: 'error', text: `Failed to ${action} upload` });
    }
  };

  const handleViewViolations = (uploadId) => {
    setViolationFilters(prev => ({ ...prev, uploadId }));
    setActiveTab('violations');
  };

  const handleUpdateViolationStatus = async (violationId, newStatus) => {
    try {
      await axios.put(`/api/time-audit/violations/${violationId}/status`, { status: newStatus });
      setMessage({ type: 'success', text: 'Violation status updated' });
      setEditingViolationId(null);
      fetchViolations();
      fetchUploads();
      fetchSummary();
      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error updating violation status:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update violation status' });
    }
  };

  const handleMarkSalaried = async (violation, e) => {
    e.stopPropagation();
    const employeeName = violation.employeeName || violation.employeeId;
    if (!window.confirm(
      `Mark "${employeeName}" as salaried (exempt)? This will permanently delete ALL their violations at this location — including any that have already been resolved (with manager notes) — and they will not be restored unless you unmark them as salaried.`
    )) {
      return;
    }
    setMarkingSalariedId(violation.id);
    try {
      await axios.post('/api/time-audit/salaried-employees', {
        employeeId: violation.employeeId,
        locationId: violation.locationId,
        employeeName: violation.employeeName
      });
      setMessage({ type: 'success', text: `${employeeName} marked as salaried. Pending violations dismissed.` });
      fetchViolations();
      fetchSummary();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error marking employee as salaried:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to mark employee as salaried' });
    } finally {
      setMarkingSalariedId(null);
    }
  };

  const fetchViolationDetails = async (violationId) => {
    if (violationDetails[violationId]) {
      return; // Already fetched
    }
    try {
      console.log('Fetching violation details for:', violationId);
      const response = await axios.get(`/api/time-audit/violations/${violationId}`);
      console.log('Violation details response:', response.data);
      setViolationDetails(prev => ({ ...prev, [violationId]: response.data }));
    } catch (error) {
      console.error('Error fetching violation details:', error);
      // Set an error state so it doesn't keep spinning
      setViolationDetails(prev => ({
        ...prev,
        [violationId]: {
          error: true,
          message: error.response?.data?.error || error.message,
          shiftDetails: { entries: [], shiftStart: null, shiftEnd: null, totalBreakMinutes: 0 }
        }
      }));
    }
  };

  const toggleViolationExpanded = async (violationId) => {
    if (expandedViolationId === violationId) {
      setExpandedViolationId(null);
    } else {
      setExpandedViolationId(violationId);
      await fetchViolationDetails(violationId);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' });
  };

  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#fef3c7', color: '#92400e', icon: Clock },
      processing: { bg: '#dbeafe', color: '#1e40af', icon: RefreshCw },
      completed: { bg: '#dcfce7', color: '#166534', icon: CheckCircle },
      failed: { bg: '#fee2e2', color: '#991b1b', icon: XCircle },
      valid: { bg: '#fee2e2', color: '#991b1b', icon: AlertTriangle },
      corrected: { bg: '#dcfce7', color: '#166534', icon: CheckCircle }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: style.bg,
        color: style.color,
        fontSize: '12px',
        fontWeight: '500',
        textTransform: 'capitalize'
      }}>
        <Icon size={12} />
        {status}
      </span>
    );
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      warning: { bg: '#fef3c7', color: '#92400e' },
      low: { bg: '#dbeafe', color: '#1e40af' },
      medium: { bg: '#fef3c7', color: '#92400e' },
      high: { bg: '#fee2e2', color: '#991b1b' }
    };
    const style = colors[severity] || colors.medium;

    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '4px',
        backgroundColor: style.bg,
        color: style.color,
        fontSize: '11px',
        fontWeight: '500',
        textTransform: 'uppercase'
      }}>
        {severity}
      </span>
    );
  };

  const formatViolationType = (type) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getResolvedPercentage = (upload) => {
    if (!upload.totalViolations || upload.totalViolations === 0) return 100;
    const resolved = upload.resolvedViolations || 0;
    return Math.round((resolved / upload.totalViolations) * 100);
  };

  const getExportData = () => {
    return violations.map(v => ({
      'Employee Name': v.employeeName || v.employeeId,
      'Employee ID': v.employeeId,
      'Location': v.location?.locationNumber || '-',
      'Date': v.workDate ? new Date(v.workDate + 'T12:00:00').toLocaleDateString('en-US') : '-',
      'Violation Type': formatViolationType(v.violationType),
      'Severity': (v.severity || '').charAt(0).toUpperCase() + (v.severity || '').slice(1),
      'Description': v.description || '',
      'Shift Hours': v.shiftHours ? parseFloat(v.shiftHours).toFixed(1) : '-',
      'Hourly Rate': v.hourlyRate ? `$${parseFloat(v.hourlyRate).toFixed(2)}` : '-',
      'Potential Penalty': v.potentialPenalty ? `$${parseFloat(v.potentialPenalty).toFixed(2)}` : '$0.00',
      'Status': (v.status || '').charAt(0).toUpperCase() + (v.status || '').slice(1),
    }));
  };

  const exportToExcel = () => {
    const data = getExportData();
    if (data.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(data);
    // Set column widths
    ws['!cols'] = [
      { wch: 25 }, { wch: 12 }, { wch: 10 }, { wch: 12 },
      { wch: 22 }, { wch: 10 }, { wch: 50 }, { wch: 10 },
      { wch: 12 }, { wch: 14 }, { wch: 10 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Violations');
    const dateStr = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `violations-report-${dateStr}.xlsx`);
  };

  const exportToPDF = () => {
    const data = getExportData();
    if (data.length === 0) return;
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16);
    doc.text('Time Audit - Violations Report', 14, 15);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-US')} | ${data.length} violation${data.length !== 1 ? 's' : ''}`, 14, 22);

    // Summary by status
    const pending = violations.filter(v => v.status === 'pending').length;
    const valid = violations.filter(v => v.status === 'valid').length;
    const corrected = violations.filter(v => v.status === 'corrected').length;
    const totalPenalty = violations.reduce((sum, v) => sum + (parseFloat(v.potentialPenalty) || 0), 0);
    doc.text(`Pending: ${pending} | Valid: ${valid} | Corrected: ${corrected} | Total Potential Penalty: $${totalPenalty.toFixed(2)}`, 14, 28);

    autoTable(doc, {
      startY: 33,
      head: [['Employee', 'Location', 'Date', 'Type', 'Severity', 'Hours', 'Penalty', 'Status']],
      body: data.map(row => [
        row['Employee Name'],
        row['Location'],
        row['Date'],
        row['Violation Type'],
        row['Severity'],
        row['Shift Hours'],
        row['Potential Penalty'],
        row['Status']
      ]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [59, 130, 246], fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 40 },
        3: { cellWidth: 35 },
        6: { halign: 'right' },
      }
    });

    const dateStr = new Date().toISOString().split('T')[0];
    doc.save(`violations-report-${dateStr}.pdf`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <FileSpreadsheet className="h-8 w-8 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Time Audit</h2>
            <p className="text-sm text-gray-600">Manage time clock uploads and violations</p>
          </div>
        </div>
        {!isViewer && (
          <div className="mt-3 sm:mt-0">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Upload File'}
            </button>
          </div>
        )}
      </div>

      {message.text && (
        <div className={`rounded-md p-4 ${message.type === 'error' ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
          <div className="flex">
            {message.type === 'success' ? <CheckCircle className="h-5 w-5 text-green-400" /> : <XCircle className="h-5 w-5 text-red-400" />}
            <span className="ml-3 text-sm font-medium" style={{ color: message.type === 'error' ? '#991b1b' : '#166534' }}>{message.text}</span>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-4">
            <div className="text-2xl font-bold text-primary-600">{summary.total}</div>
            <div className="text-sm text-gray-600">Total Violations</div>
          </div>
          <div className="bg-amber-50 rounded-xl shadow-soft border border-amber-200 p-4">
            <div className="text-2xl font-bold text-amber-700">
              {summary.byStatus?.find(s => s.status === 'pending')?.count || 0}
            </div>
            <div className="text-sm text-amber-700">Pending Review</div>
          </div>
          <div className="bg-red-50 rounded-xl shadow-soft border border-red-200 p-4">
            <div className="text-2xl font-bold text-red-700">
              {summary.byStatus?.find(s => s.status === 'valid')?.count || 0}
            </div>
            <div className="text-sm text-red-700">Valid</div>
          </div>
          <div className="bg-green-50 rounded-xl shadow-soft border border-green-200 p-4">
            <div className="text-2xl font-bold text-green-700">
              {summary.byStatus?.find(s => s.status === 'corrected')?.count || 0}
            </div>
            <div className="text-sm text-green-700">Corrected</div>
          </div>
          <div className="bg-red-50 rounded-xl shadow-soft border border-red-200 p-4">
            <div className="text-2xl font-bold text-red-700">
              ${summary.totalPotentialPenalty ? parseFloat(summary.totalPotentialPenalty).toFixed(2) : '0.00'}
            </div>
            <div className="text-sm text-red-700">Potential Penalties</div>
          </div>
          <div className="bg-green-50 rounded-xl shadow-soft border border-green-200 p-4">
            <div className="text-2xl font-bold text-green-700">
              ${summary.totalPenaltiesSaved ? parseFloat(summary.totalPenaltiesSaved).toFixed(2) : '0.00'}
            </div>
            <div className="text-sm text-green-700">Penalties Saved</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('uploads');
              setViolationFilters(prev => ({ ...prev, uploadId: '' }));
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'uploads'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Uploads ({uploads.length})
          </button>
          <button
            onClick={() => setActiveTab('violations')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === 'violations'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Violations ({summary?.total ?? 0})
            {violationFilters.uploadId && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                Filtered
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('employees')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === 'employees'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="w-4 h-4 mr-2" />
            Employees
          </button>
        </nav>
      </div>

      {/* Uploads Tab */}
      {activeTab === 'uploads' && (
        <div className="bg-white rounded-xl shadow-soft overflow-hidden">
          {/* Include Archived Filter */}
          <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-end">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeArchived"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
              />
              <label htmlFor="includeArchived" className="text-sm text-gray-700 cursor-pointer">
                Include Archived
              </label>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : uploads.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No uploads yet. Upload a time clock file to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Entries</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Violations</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Resolved</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {uploads.map(upload => (
                    <tr key={upload.id} className={`hover:bg-gray-50 transition-colors duration-150 ${upload.isActive === false ? 'bg-gray-50 opacity-60' : ''}`}>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">{upload.originalFileName}</div>
                          {upload.isActive === false && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                              Archived
                            </span>
                          )}
                        </div>
                        {upload.emailFrom && (
                          <div className="text-xs text-gray-500">via {upload.emailFrom}</div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {upload.location ? (
                          <div className="flex items-center text-sm">
                            <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                            <span className="font-medium">{upload.location.locationNumber}</span>
                            {upload.location.locationName && (
                              <span className="text-gray-500 ml-1 truncate max-w-[150px]">
                                - {upload.location.locationName}
                              </span>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {upload.payPeriodStart && upload.payPeriodEnd ? (
                          `${new Date(upload.payPeriodStart + 'T12:00:00').toLocaleDateString('en-US')} - ${new Date(upload.payPeriodEnd + 'T12:00:00').toLocaleDateString('en-US')}`
                        ) : '-'}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {upload.totalEntries}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        {upload.totalViolations > 0 ? (
                          <button
                            onClick={() => handleViewViolations(upload.id)}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200 transition-colors"
                          >
                            {upload.totalViolations}
                          </button>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            0
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex flex-col items-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            getResolvedPercentage(upload) === 100
                              ? 'bg-green-100 text-green-800'
                              : getResolvedPercentage(upload) >= 50
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}>
                            {upload.resolvedViolations || 0}/{upload.totalViolations || 0}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">
                            {getResolvedPercentage(upload)}%
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {new Date(upload.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => {
                              setEmployeesTabUploadId(upload.id);
                              setActiveTab('employees');
                            }}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                            title="View employee shifts"
                          >
                            <Users className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleViewViolations(upload.id)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="View violations"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {!isViewer && (
                            <div className="relative">
                              <button
                                className="dropdown-trigger p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                                onClick={(e) => {
                                  if (openDropdownId === upload.id) {
                                    setOpenDropdownId(null);
                                    setOpenDropdownUpload(null);
                                  } else {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const viewportHeight = window.innerHeight;
                                    const dropdownHeight = 88; // Height of dropdown (2 items * 40px + padding)
                                    const dropdownWidth = 192; // w-48 = 12rem = 192px
                                    const spaceBelow = viewportHeight - rect.bottom;
                                    const spaceRight = window.innerWidth - rect.right;

                                    // Position above if not enough space below
                                    const shouldShowAbove = spaceBelow < dropdownHeight + 8;

                                    // Calculate left position - align right edge of dropdown with right edge of button
                                    let leftPos = rect.right - dropdownWidth;
                                    if (leftPos < 8) leftPos = 8; // Don't go off left edge

                                    setDropdownPosition({
                                      top: shouldShowAbove
                                        ? rect.top - dropdownHeight - 4
                                        : rect.bottom + 4,
                                      left: leftPos
                                    });
                                    setOpenDropdownId(upload.id);
                                    setOpenDropdownUpload(upload);
                                  }
                                }}
                                title="More actions"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Dropdown Menu */}
      {openDropdownId && openDropdownUpload && (
        <div
          className="dropdown-menu fixed w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
              onClick={() => {
                setOpenDropdownId(null);
                setOpenDropdownUpload(null);
                handleArchiveUpload(openDropdownUpload.id, openDropdownUpload.originalFileName, !openDropdownUpload.isActive);
              }}
            >
              <Archive className="w-4 h-4 mr-2" />
              {openDropdownUpload.isActive !== false ? 'Archive Upload' : 'Unarchive Upload'}
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
              onClick={() => {
                setOpenDropdownId(null);
                setOpenDropdownUpload(null);
                handleDeleteUpload(openDropdownUpload.id, openDropdownUpload.originalFileName);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Upload
            </button>
          </div>
        </div>
      )}

      {/* Violations Tab */}
      {activeTab === 'violations' && (
        <div className="bg-white rounded-xl shadow-soft overflow-hidden">
          {/* Filters */}
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-wrap gap-4">
              {violationFilters.uploadId && (
                <button
                  onClick={() => setViolationFilters(prev => ({ ...prev, uploadId: '' }))}
                  className="inline-flex items-center px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm hover:bg-blue-200 transition-colors"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Clear Upload Filter
                </button>
              )}
              <select
                value={violationFilters.status}
                onChange={(e) => setViolationFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="valid">Valid</option>
                <option value="corrected">Corrected</option>
              </select>
              <select
                value={violationFilters.severity}
                onChange={(e) => setViolationFilters(prev => ({ ...prev, severity: e.target.value }))}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Severities</option>
                <option value="warning">Warning</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
              <select
                value={violationFilters.violationType}
                onChange={(e) => setViolationFilters(prev => ({ ...prev, violationType: e.target.value }))}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Types</option>
                <option value="meal_break_missing">Meal Break Missing</option>
                <option value="meal_break_short">Meal Break Short</option>
                <option value="meal_break_late">Meal Break Late</option>
                <option value="second_meal_missing">Second Meal Missing</option>
                <option value="second_meal_short">Second Meal Short</option>
                <option value="second_meal_late">Second Meal Late</option>
                <option value="extended_work_without_break">Extended Work Without Break</option>
                <option value="paid_break_too_long">Paid Break Too Long</option>
              </select>
              <div className="flex gap-2 ml-auto">
                <button
                  onClick={exportToExcel}
                  disabled={violations.length === 0}
                  className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Excel
                </button>
                <button
                  onClick={exportToPDF}
                  disabled={violations.length === 0}
                  className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4 mr-1" />
                  PDF
                </button>
              </div>
            </div>
          </div>

          {violations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No violations found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Penalty</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    {!isViewer && (
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {violations.map(violation => (
                    <React.Fragment key={violation.id}>
                      <tr
                        className={`hover:bg-gray-50 transition-colors duration-150 cursor-pointer ${expandedViolationId === violation.id ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleViolationExpanded(violation.id)}
                      >
                        <td className="px-2 py-4">
                          <button className="p-1 text-gray-500 hover:text-gray-700">
                            {expandedViolationId === violation.id ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">{violation.employeeName || violation.employeeId}</div>
                          <div className="text-xs text-gray-500">ID: {violation.employeeId}</div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {violation.location?.locationNumber || '-'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {new Date(violation.workDate + 'T12:00:00').toLocaleDateString('en-US')}
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">{formatViolationType(violation.violationType)}</div>
                          <div className="text-xs text-gray-500 max-w-[200px] truncate" title={violation.description}>
                            {violation.description}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          {getSeverityBadge(violation.severity)}
                        </td>
                        <td className="px-4 py-4 text-center text-sm text-gray-900">
                          {violation.shiftHours ? parseFloat(violation.shiftHours).toFixed(1) : '-'}
                        </td>
                        <td className="px-4 py-4 text-right text-sm font-medium text-red-600">
                          ${violation.potentialPenalty ? parseFloat(violation.potentialPenalty).toFixed(2) : '0.00'}
                        </td>
                        <td className="px-4 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                          {editingViolationId === violation.id ? (
                            <select
                              value={violation.status}
                              onChange={(e) => handleUpdateViolationStatus(violation.id, e.target.value)}
                              onBlur={() => setEditingViolationId(null)}
                              autoFocus
                              className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              <option value="pending">Pending</option>
                              <option value="valid">Valid</option>
                              <option value="corrected">Corrected</option>
                            </select>
                          ) : (
                            getStatusBadge(violation.status)
                          )}
                        </td>
                        {!isViewer && (
                          <td className="px-4 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => setEditingViolationId(violation.id)}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                                title="Edit status"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              {violation.status === 'pending' && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs font-medium text-gray-500">
                                    Salaried
                                  </span>
                                  <button
                                    onClick={(e) => handleMarkSalaried(violation, e)}
                                    disabled={markingSalariedId === violation.id}
                                    className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors bg-gray-300 hover:bg-gray-400 disabled:opacity-50"
                                    title="Mark employee as salaried (exempt from violations)"
                                  >
                                    <span className="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform translate-x-0.5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                      {/* Expanded Details Row */}
                      {expandedViolationId === violation.id && (
                        <tr className="bg-blue-50">
                          <td colSpan={isViewer ? 9 : 10} className="px-4 py-4">
                            <div className="bg-white rounded-lg border border-blue-200 p-4">
                              {violationDetails[violation.id]?.error ? (
                                <div className="text-center text-red-500 py-4">
                                  <AlertTriangle className="w-5 h-5 mx-auto mb-2" />
                                  Error loading details: {violationDetails[violation.id].message}
                                </div>
                              ) : violationDetails[violation.id]?.shiftDetails ? (
                                <div className="space-y-4">
                                  {/* Shift Overview */}
                                  <div className="flex items-center gap-6 text-sm">
                                    <div>
                                      <span className="text-gray-500">Shift:</span>
                                      <span className="ml-2 font-medium">
                                        {formatTime(violationDetails[violation.id].shiftDetails.shiftStart)} - {formatTime(violationDetails[violation.id].shiftDetails.shiftEnd)}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Total Hours:</span>
                                      <span className="ml-2 font-medium">{violation.shiftHours ? parseFloat(violation.shiftHours).toFixed(1) : '-'}h</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Break Time:</span>
                                      <span className="ml-2 font-medium">{formatDuration(violationDetails[violation.id].shiftDetails.totalBreakMinutes)}</span>
                                    </div>
                                  </div>

                                  {/* Violation Details */}
                                  {violationDetails[violation.id].details && (
                                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                      <div className="text-sm font-medium text-red-800 mb-1">Violation Details</div>
                                      <div className="text-sm text-red-700">
                                        {violationDetails[violation.id].details.requiredByHour && (
                                          <div>Break required by hour: <strong>{violationDetails[violation.id].details.requiredByHour}</strong></div>
                                        )}
                                        {violationDetails[violation.id].details.breakStartedAtHour && (
                                          <div>Break actually started at hour: <strong>{violationDetails[violation.id].details.breakStartedAtHour.toFixed(1)}</strong>
                                            {violationDetails[violation.id].details.breakStartTime && (
                                              <span> ({new Date(violationDetails[violation.id].details.breakStartTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' })})</span>
                                            )}
                                          </div>
                                        )}
                                        {violationDetails[violation.id].details.actualBreakMinutes !== undefined && (
                                          <div>Break duration: <strong>{violationDetails[violation.id].details.actualBreakMinutes.toFixed(0)} minutes</strong> (required: {violationDetails[violation.id].details.requiredMinutes} minutes)</div>
                                        )}
                                        {violationDetails[violation.id].details.totalHoursWorked && (
                                          <div>Total hours worked: <strong>{violationDetails[violation.id].details.totalHoursWorked.toFixed(1)}h</strong></div>
                                        )}
                                        {violationDetails[violation.id].details.reasonDetail && (
                                          <div className="text-red-600 mt-1 font-medium">{violationDetails[violation.id].details.reasonDetail}</div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Shift Timeline */}
                                  <div>
                                    <div className="text-sm font-medium text-gray-700 mb-2">Shift Timeline</div>
                                    <div className="space-y-2">
                                      {violationDetails[violation.id].shiftDetails.entries.map((entry, idx) => {
                                        const isBreak = entry.entryType === 'unpaid_break' || entry.entryType === 'paid_break';
                                        const isViolatingSegment = violation.violationType.includes('break') && isBreak;
                                        return (
                                          <div
                                            key={entry.id || idx}
                                            className={`flex items-center gap-3 p-2 rounded-md ${
                                              isViolatingSegment
                                                ? 'bg-red-100 border border-red-300'
                                                : isBreak
                                                  ? 'bg-amber-50 border border-amber-200'
                                                  : 'bg-green-50 border border-green-200'
                                            }`}
                                          >
                                            <div className={`p-1.5 rounded-full ${isBreak ? 'bg-amber-200' : 'bg-green-200'}`}>
                                              {isBreak ? <Coffee className="w-3 h-3 text-amber-700" /> : <Play className="w-3 h-3 text-green-700" />}
                                            </div>
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2">
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                                  isBreak ? 'bg-amber-200 text-amber-800' : 'bg-green-200 text-green-800'
                                                }`}>
                                                  {entry.entryType === 'work' ? 'WORK' : entry.entryType === 'unpaid_break' ? 'BREAK' : 'PAID BREAK'}
                                                </span>
                                                {isViolatingSegment && (
                                                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-red-200 text-red-800">
                                                    ISSUE
                                                  </span>
                                                )}
                                              </div>
                                              <div className="text-sm text-gray-700 mt-1">
                                                {formatTime(entry.clockIn)} - {formatTime(entry.clockOut)}
                                                <span className="mx-2 text-gray-400">•</span>
                                                <span className="font-medium">{formatDuration(entry.duration)}</span>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center text-gray-500 py-4">
                                  <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                                  Loading shift details...
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Employees Tab */}
      {activeTab === 'employees' && (
        <div className="space-y-4">
          {/* Filters Row */}
          <div className="bg-white rounded-xl shadow-soft p-4 border border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Upload Selector Dropdown */}
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                  Upload
                </label>
                <select
                  value={employeesTabUploadId || 'all'}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEmployeesTabUploadId(val === 'all' ? 'all' : val);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Uploads</option>
                  {uploads.map(upload => (
                    <option key={upload.id} value={upload.id}>
                      {upload.originalFileName}
                      {upload.location ? ` — ${upload.location.locationNumber}` : ''}
                      {upload.payPeriodStart && upload.payPeriodEnd
                        ? ` (${new Date(upload.payPeriodStart + 'T12:00:00').toLocaleDateString('en-US')} - ${new Date(upload.payPeriodEnd + 'T12:00:00').toLocaleDateString('en-US')})`
                        : ''}
                    </option>
                  ))}
                </select>
              </div>
              {/* Employee Search */}
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                  Search Employee
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    placeholder="Search by employee name..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {employeeSearch && (
                    <button
                      onClick={() => setEmployeeSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Employee Detail View */}
          {employeesTabUploadId ? (
            <UploadDetailView
              uploadId={employeesTabUploadId}
              embedded={true}
              searchFilter={employeeSearch}
            />
          ) : (
            <div className="text-center py-12 text-gray-500 bg-white rounded-xl shadow-soft border border-gray-200">
              {uploads.length === 0
                ? 'No uploads available. Upload a time clock file first.'
                : 'Select an upload above to view employee details.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeAuditUploads;
